import React, { useEffect, useState } from 'react';
import { Button, Icon, Input, Modal, Popconfirm, Skeleton, Tag, Tooltip } from 'antd';
import styled from 'styled-components';

import { request } from '~/components/Instance';
import { getCategoryLabel } from '~/_domains/societario/helpers';
import { applyStagesOverview } from '~/_domains/societario/stagesConfig';

// Etapas do processo societário por tipo de ticket.
//
// Cada escritório monta a sua lista. A etapa é identificada pela `key`, que o
// servidor gera na criação e nunca troca: renomear não apaga o que os tickets
// já marcaram, e remover só tira a etapa da tela e da contagem.
export const SOCIETARIO_STAGE_TYPES = {
  abertura: 1,
  alteracao: 2,
  encerramento: 3,
  'troca-entrada': 5,
  'troca-saida': 7,
  tarefa: 4,
  outros: 6,
};

const MAX_SHORT = 6;
const MAX_STAGES = 15;

const Intro = styled.p`
  color: #595959;
  margin-bottom: 16px;
`;

const Preview = styled.div`
  display: flex;
  border-radius: 3px;
  overflow: hidden;
  margin-bottom: 20px;
  max-width: 420px;
`;

const Chip = styled.span`
  flex: 1;
  min-width: 0;
  text-align: center;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.3px;
  line-height: 17px;
  padding: 0 2px;
  overflow: hidden;
  white-space: nowrap;
  background: #f5f5f5;
  color: #8c8c8c;

  & + & {
    margin-left: 1px;
  }
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
`;

const Position = styled.span`
  width: 28px;
  flex-shrink: 0;
  color: #8c8c8c;
  font-weight: 600;
`;

const Removed = styled.div`
  margin-top: 20px;
  padding: 12px 14px;
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  background: #fafafa;
`;

const RemovedTitle = styled.div`
  font-size: 11px;
  color: #8c8c8c;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 6px;
`;

const RemovedRow = styled.div`
  display: flex;
  align-items: center;
  padding: 4px 0;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 20px;
`;

const sameList = (a, b) => (
  a.length === b.length &&
  a.every((stage, index) => (
    stage.key === b[index].key &&
    stage.label === b[index].label &&
    stage.short === b[index].short
  ))
);

const toRows = (stages) => (stages || []).map((stage) => ({
  key: stage.key,
  label: stage.label,
  short: stage.short,
}));

export const SocietarioStages = (props) => {
  const category = SOCIETARIO_STAGE_TYPES[props.type] || 2;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [entry, setEntry] = useState(null);
  const [rows, setRows] = useState([]);

  const applyOverview = (overview) => {
    const current = overview && overview.categories && overview.categories[category];
    applyStagesOverview(overview);
    setEntry(current || null);
    setRows(toRows(current && current.stages));
  };

  useEffect(() => {
    setLoading(true);
    request('get', '/societario/stages?usage=1', {}, false)
      .then((response) => applyOverview(response.data && response.data.data))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  const update = (index, field, value) => {
    setRows(rows.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  };

  const move = (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= rows.length) return;
    const next = rows.slice();
    const moved = next[index];
    next[index] = next[target];
    next[target] = moved;
    setRows(next);
  };

  const removeNow = (index) => {
    setRows(rows.filter((row, i) => i !== index));
  };

  // Antes de tirar uma etapa que tickets em aberto já marcaram, avisa quantos:
  // a marcação não se perde, mas some da tela e da contagem.
  const remove = (index) => {
    const row = rows[index];
    const usage = (entry && entry.usage) || {};
    const count = row && row.key ? usage[row.key] || 0 : 0;

    if (count === 0) {
      removeNow(index);
      return;
    }

    Modal.confirm({
      title: 'Remover "' + row.label + '"?',
      content: count + (count === 1 ? ' ticket em aberto já marcou' : ' tickets em aberto já marcaram') +
        ' esta etapa. A marcação fica guardada, mas sai do ticket e da contagem.' +
        ' Se a etapa voltar (em "Etapas removidas"), volta marcada.',
      okText: 'Remover',
      okType: 'danger',
      cancelText: 'Cancelar',
      // O drawer de Configurações fica em 1010; o padrão do Modal (1000) o
      // deixaria escondido atrás dele.
      zIndex: 1100,
      onOk: () => removeNow(index),
    });
  };

  // Volta com a mesma chave: as marcações antigas reaparecem.
  const restore = (stage) => {
    setRows(rows.concat([{ key: stage.key, label: stage.label, short: stage.short }]));
  };

  const add = () => {
    setRows(rows.concat([{ key: null, label: '', short: '' }]));
  };

  const rowKeys = rows.map((row) => row.key).filter((key) => !!key);
  const removedStages = ((entry && entry.removed) || []).filter((stage) => rowKeys.indexOf(stage.key) === -1);
  const invalid = rows.length === 0 || rows.some((row) => !row.label.trim() || !row.short.trim());
  const dirty = !!entry && !sameList(rows, toRows(entry.stages));

  const save = () => {
    setSaving(true);
    request('put', '/societario/stages/' + category, {
      stages: rows.map((row) => ({
        key: row.key || undefined,
        label: row.label.trim(),
        short: row.short.trim().toUpperCase(),
      })),
    })
      .then((response) => applyOverview(response.data && response.data.data))
      .finally(() => setSaving(false));
  };

  const reset = () => {
    setSaving(true);
    request('delete', '/societario/stages/' + category)
      .then((response) => applyOverview(response.data && response.data.data))
      .finally(() => setSaving(false));
  };

  if (loading) {
    return <Skeleton active paragraph={{ rows: 6 }} />;
  }

  if (!entry) {
    return <Intro>Não foi possível carregar as etapas. Tente de novo em instantes.</Intro>;
  }

  return (
    <div>
      <Intro>
        Etapas que aparecem no ticket e nos chips do card de <strong>{getCategoryLabel(category)}</strong>.{' '}
        {entry.customized
          ? <Tag color="purple">Personalizada</Tag>
          : <Tag>Padrão Contlabs</Tag>}
        <br />
        Renomear uma etapa mantém o que os tickets já marcaram. Remover tira a etapa do ticket e da contagem,
        sem apagar a marcação: se ela voltar, volta marcada. Tickets já concluídos não mudam: guardam a
        lista com que foram concluídos.
      </Intro>

      <Preview>
        {rows.map((row, index) => (
          <Tooltip key={(row.key || 'nova') + index} title={row.label}>
            <Chip>{(row.short || '?').toUpperCase()}</Chip>
          </Tooltip>
        ))}
      </Preview>

      {rows.map((row, index) => (
        <Row key={(row.key || 'nova') + '-' + index}>
          <Position>{index + 1}</Position>
          <Input
            value={row.label}
            placeholder="Nome da etapa"
            maxLength={120}
            onChange={(event) => update(index, 'label', event.target.value)}
            style={{ flex: 1, marginRight: 8 }}
          />
          <Tooltip title={'Sigla do card, até ' + MAX_SHORT + ' letras'}>
            <Input
              value={row.short}
              placeholder="SIGLA"
              maxLength={MAX_SHORT}
              onChange={(event) => update(index, 'short', event.target.value.toUpperCase())}
              style={{ width: 96, marginRight: 8 }}
            />
          </Tooltip>
          <Button icon="arrow-up" disabled={index === 0} onClick={() => move(index, -1)} style={{ marginRight: 4 }} />
          <Button icon="arrow-down" disabled={index === rows.length - 1} onClick={() => move(index, 1)} style={{ marginRight: 4 }} />
          <Tooltip title="Remover etapa">
            <Button icon="delete" type="danger" ghost disabled={rows.length === 1} onClick={() => remove(index)} />
          </Tooltip>
        </Row>
      ))}

      <Button type="dashed" icon="plus" onClick={add} disabled={rows.length >= MAX_STAGES} style={{ marginTop: 12, width: '100%' }}>
        Adicionar etapa
      </Button>

      {removedStages.length > 0 && (
        <Removed>
          <RemovedTitle>Etapas removidas</RemovedTitle>
          {removedStages.map((stage) => (
            <RemovedRow key={stage.key}>
              <Tag>{stage.short}</Tag>
              <span style={{ flex: 1, color: '#595959' }}>{stage.label}</span>
              <Button size="small" icon="undo" onClick={() => restore(stage)} disabled={rows.length >= MAX_STAGES}>
                Trazer de volta
              </Button>
            </RemovedRow>
          ))}
        </Removed>
      )}

      <Footer>
        <Popconfirm
          title="Voltar este tipo para a lista padrão? O que os tickets já marcaram não muda."
          okText="Restaurar"
          cancelText="Cancelar"
          onConfirm={reset}
          disabled={!entry.customized}
        >
          <Button icon="rollback" disabled={!entry.customized || saving}>
            Restaurar padrão
          </Button>
        </Popconfirm>
        <Button type="primary" icon="save" loading={saving} disabled={!dirty || invalid} onClick={save}>
          {invalid ? 'Preencha nome e sigla' : 'Salvar etapas'}
        </Button>
      </Footer>
      {dirty && (
        <p style={{ color: '#1890ff', marginTop: 8, textAlign: 'right' }}>
          <Icon type="exclamation-circle" /> Alterações não salvas
        </p>
      )}
    </div>
  );
};

export default SocietarioStages;
